export type SheetModel = 'field' | 'goalie' | 'faceoff' | 'defense';

export const NPC_FAQ_URL = 'https://statdog.live/faq?section=npc';
export const STATDOG_SITE_URL = 'https://statdog.live';
export const STATDOG_COPYRIGHT = 'Copyright © 2026 Sports Data Partners, LLC dba STATDOG';
export const STATDOG_CTA_LABEL = 'Get STATDOG for Your Team at https://statdog.live';

export const formatPosition = (position?: string | string[]) => {
  if (!position) return '-';
  const list = Array.isArray(position) ? position : [position];
  return list
    .map((pos) =>
      pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
    )
    .join(' | ');
};

export const formatClassYear = (graduation?: string | number) => {
  if (graduation == null || graduation === '') return '';
  const text = String(graduation);
  const year = text.match(/(\d{4})/)?.[1] || text;
  if (/^\d{4}$/.test(year)) return `Class of '${year.slice(2)}`;
  return `Class of ${text}`;
};

export const formatSigned = (value?: number | null, digits = 0) => {
  const n = Number(value ?? 0);
  const formatted = digits > 0 ? n.toFixed(digits) : String(Math.round(n));
  if (n > 0) return `+${formatted}`;
  return formatted;
};

export const perGame = (value?: number | null, games?: number | null, digits = 2) => {
  const gp = Number(games || 0);
  if (gp <= 0) return (0).toFixed(digits);
  return (Number(value || 0) / gp).toFixed(digits);
};

export const computeNpc = (stats?: {
  groundballs?: number;
  turnovers?: number;
  causedturnover?: number;
  shotsaved?: number;
} | null) => {
  const gb = stats?.groundballs || 0;
  const to = stats?.turnovers || 0;
  const cto = stats?.causedturnover || 0;
  const ss = stats?.shotsaved || 0;
  return gb - (to - cto) - ss;
};

export const computeGoalsPlusMinus = (npc: number) => Math.round(npc * 0.25 * 100) / 100;

const parseSeasonDateParts = (value?: string | Date | null) => {
  if (!value) return null;
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate() };
};

/** Regular varsity season for career totals: January through September. */
export const isRegularSpringSeason = (row?: {
  startDate?: string | Date | null;
  endDate?: string | Date | null;
  isTournament?: boolean;
  isRegularSeason?: boolean;
}) => {
  if (row?.isRegularSeason === true) return true;
  if (row?.isRegularSeason === false || row?.isTournament === true) return false;
  const start = parseSeasonDateParts(row?.startDate);
  const end = parseSeasonDateParts(row?.endDate);
  if (!start && !end) return false;
  const inCareerWindow = (parts: { month: number } | null) =>
    !parts || (parts.month >= 1 && parts.month <= 9);
  return inCareerWindow(start) && inCareerWindow(end);
};

export const seasonYear = (row?: { startDate?: string | null; seasonName?: string }) => {
  const start = parseSeasonDateParts(row?.startDate);
  if (start?.year) return String(start.year);
  const match = String(row?.seasonName || '').match(/20\d{2}/);
  return match?.[0] || '';
};

const FACEOFF_RE = /face\s*-?\s*off|^fo$|faceoff/i;
const GOALIE_RE = /goalie|^g$/i;
const DEFENSE_RE = /defense|long\s*stick|\blsm\b|short\s*stick|\bssdm\b|\bssm\b/i;

export const classifySheetModel = (position?: string | string[], isGoalie?: boolean): SheetModel => {
  const list = (Array.isArray(position) ? position : [position]).filter(Boolean) as string[];
  if (isGoalie || list.some((pos) => GOALIE_RE.test(pos))) return 'goalie';
  if (list.some((pos) => FACEOFF_RE.test(pos))) return 'faceoff';
  if (list.some((pos) => DEFENSE_RE.test(pos))) return 'defense';
  return 'field';
};
